<script lang="ts">
  import { page, Link, router } from '@inertiajs/svelte'
  import type { Snippet } from 'svelte'
  import type { CurrentUser, FlashData } from '../types'
  import PublicLayout from './PublicLayout.svelte'

  type Props = {
    current_user?: CurrentUser | null
    flash?: FlashData
    children: Snippet
  }

  let { current_user, flash, children }: Props = $props()

  // 64px 아이콘 사이드바 — 확정 정본 .planning/biseo-redesign.md §3
  // 라벨이 없으므로 title·aria-label 이 이름을 대신한다.
  const navItems = [
    { href: '/', label: '구독', icon: 'card' },
    { href: '/calendar', label: '캘린더', icon: 'calendar' },
    { href: '/inbox', label: '받은 메일', icon: 'mail' },
    { href: '/mailboxes', label: '메일함 연결', icon: 'link' },
    { href: '/exclusions', label: '제외됨', icon: 'exclude' },
  ]

  let menuOpen = $state(false)

  // 다크 모드 — 시스템 설정을 기본으로 따르되 수동 토글이 이긴다.
  // localStorage 읽기/쓰기는 프라이빗 모드에서 throw 할 수 있어 try/catch 로 감싼다.
  type ThemePref = 'light' | 'dark' | null
  function readStoredTheme(): ThemePref {
    try {
      const v = localStorage.getItem('theme')
      return v === 'light' || v === 'dark' ? v : null
    } catch {
      return null
    }
  }
  let theme = $state<ThemePref>(readStoredTheme())
  const effectiveTheme = $derived(
    theme ?? (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'),
  )

  function applyTheme(next: ThemePref) {
    theme = next
    try {
      if (next) localStorage.setItem('theme', next)
      else localStorage.removeItem('theme')
    } catch {
      // 프라이빗 모드 등 — 저장 실패해도 이번 세션 표시는 유지한다
    }
    if (typeof document !== 'undefined') {
      if (next) document.documentElement.dataset.theme = next
      else delete document.documentElement.dataset.theme
    }
  }

  function toggleTheme() {
    applyTheme(effectiveTheme === 'dark' ? 'light' : 'dark')
  }

  function isActive(href: string, url: string) {
    const path = url.split('?')[0]
    return href === '/' ? path === '/' : path.startsWith(href)
  }

  function logout() {
    router.delete('/logout')
  }

  const initial = $derived((current_user?.display_name || current_user?.email || '?').trim().charAt(0).toUpperCase())
  const currentLabel = $derived(navItems.find((i) => isActive(i.href, page.url))?.label ?? '구독비서')
</script>

{#if !current_user}
  <PublicLayout {flash}>
    {@render children()}
  </PublicLayout>
{:else}
  <div class="flex min-h-screen bg-[var(--color-paper)] text-[var(--color-ink)]">
    <!-- 데스크톱: 64px 아이콘 사이드바 / 모바일: 숨김(헤더만 — 하단 탭바 없음) -->
    <aside
      class="hidden w-16 shrink-0 flex-col items-center border-r border-[var(--color-line)] bg-[var(--color-soft)] py-4 md:flex"
    >
      <Link href="/" class="mb-5 flex h-8 w-8 items-center justify-center" aria-label="구독비서 홈" title="구독비서">
        <span class="relative block h-6 w-6 rounded-[7px] bg-[var(--color-midnight)]">
          <span
            class="absolute -right-1 -bottom-1 block h-2.5 w-2.5 rounded-full border-2 border-[var(--color-soft)] bg-[var(--color-orange)]"
          ></span>
        </span>
      </Link>

      <nav class="flex flex-col items-center gap-1.5">
        {#each navItems as item (item.href)}
          {@const active = isActive(item.href, page.url)}
          <Link
            href={item.href}
            title={item.label}
            aria-label={item.label}
            aria-current={active ? 'page' : undefined}
            class="group relative flex h-10 w-10 items-center justify-center rounded-[10px] transition-colors {active
              ? 'bg-[var(--color-paper)] text-[var(--color-ink)] ring-1 ring-[var(--color-line)]'
              : 'text-[var(--color-ash)] hover:bg-black/5 hover:text-[var(--color-ink)] dark:hover:bg-white/8'}"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="h-[19px] w-[19px]"
              aria-hidden="true"
            >
              {#if item.icon === 'card'}
                <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
                <path d="M2.5 10h19" />
              {:else if item.icon === 'calendar'}
                <rect x="3.5" y="5" width="17" height="15" rx="2.5" />
                <path d="M3.5 10h17M8.5 3v4M15.5 3v4" />
              {:else if item.icon === 'mail'}
                <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
                <path d="M3.5 7l8.5 6 8.5-6" />
              {:else if item.icon === 'link'}
                <path d="M10 13.5a4 4 0 0 0 5.7.4l2.6-2.6a4 4 0 0 0-5.7-5.7l-1.5 1.5" />
                <path d="M14 10.5a4 4 0 0 0-5.7-.4L5.7 12.7a4 4 0 0 0 5.7 5.7l1.5-1.5" />
              {:else}
                <circle cx="12" cy="12" r="8.5" />
                <path d="M9.2 9.2l5.6 5.6M14.8 9.2l-5.6 5.6" />
              {/if}
            </svg>
            <!-- 라벨이 없으므로 hover 툴팁으로 이름을 보여준다 -->
            <span
              class="pointer-events-none absolute left-[52px] z-20 hidden whitespace-nowrap rounded-[6px] bg-[var(--color-midnight)] px-2 py-1 text-xs font-medium text-white group-hover:block"
            >
              {item.label}
            </span>
          </Link>
        {/each}
      </nav>

      <div class="mt-auto flex flex-col items-center gap-2">
        <button
          class="flex h-8 w-8 items-center justify-center rounded-[8px] text-[var(--color-ash)] transition-colors hover:bg-black/5 hover:text-[var(--color-ink)] dark:hover:bg-white/8"
          title={effectiveTheme === 'dark' ? '라이트 모드로' : '다크 모드로'}
          aria-label={effectiveTheme === 'dark' ? '라이트 모드로 전환' : '다크 모드로 전환'}
          onclick={toggleTheme}
        >
          {#if effectiveTheme === 'dark'}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" class="h-[18px] w-[18px]" aria-hidden="true">
              <circle cx="12" cy="12" r="4.5" />
              <path d="M12 2.5v2.2M12 19.3v2.2M4.6 4.6l1.6 1.6M17.8 17.8l1.6 1.6M2.5 12h2.2M19.3 12h2.2M4.6 19.4l1.6-1.6M17.8 6.2l1.6-1.6" />
            </svg>
          {:else}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" class="h-[18px] w-[18px]" aria-hidden="true">
              <path d="M20.5 14.2a8.5 8.5 0 1 1-10.7-10.7 7 7 0 0 0 10.7 10.7z" />
            </svg>
          {/if}
        </button>
        <!-- 설정은 사이드바 항목이 아니라 아바타를 눌러 들어간다 -->
        <Link
          href="/settings"
          class="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--color-midnight)] text-xs font-semibold text-white"
          title="설정 — {current_user.email}"
          aria-label="설정"
          aria-current={isActive('/settings', page.url) ? 'page' : undefined}
        >
          {initial}
        </Link>
        <button
          class="rounded-[6px] px-1.5 py-1 text-[10px] text-[var(--color-ash)] transition-colors hover:bg-black/5 hover:text-[var(--color-ink)] dark:hover:bg-white/8"
          onclick={logout}
        >
          로그아웃
        </button>
      </div>
    </aside>

    <div class="flex min-w-0 flex-1 flex-col">
      <!-- 모바일 헤더 — 확정: 하단 탭바 없이 헤더만 -->
      <header
        class="flex items-center justify-between border-b border-[var(--color-line)] px-4 py-3 md:hidden"
      >
        <span class="text-base font-semibold">{currentLabel}</span>
        <button
          class="rounded-[6px] px-2 py-1 text-sm text-[var(--color-stone)]"
          aria-expanded={menuOpen}
          onclick={() => (menuOpen = !menuOpen)}
        >
          메뉴
        </button>
      </header>
      {#if menuOpen}
        <nav class="flex flex-col border-b border-[var(--color-line)] md:hidden">
          {#each navItems as item (item.href)}
            <Link
              href={item.href}
              class="px-4 py-2.5 text-sm {isActive(item.href, page.url)
                ? 'font-semibold text-[var(--color-ink)]'
                : 'text-[var(--color-stone)]'}"
              onclick={() => (menuOpen = false)}
            >
              {item.label}
            </Link>
          {/each}
          <!-- 모바일에는 아바타가 없으므로 설정 진입점을 메뉴에 둔다 -->
          <Link
            href="/settings"
            class="px-4 py-2.5 text-sm {isActive('/settings', page.url)
              ? 'font-semibold text-[var(--color-ink)]'
              : 'text-[var(--color-stone)]'}"
            onclick={() => (menuOpen = false)}
          >
            설정
          </Link>
          <button class="px-4 py-2.5 text-left text-sm text-[var(--color-stone)]" onclick={logout}>
            로그아웃
          </button>
        </nav>
      {/if}

      {#if flash?.notice || flash?.alert}
        <div class="px-5 pt-4 md:px-8">
          {#if flash.notice}
            <div
              class="rounded-[6px] border border-[var(--color-line)] bg-[var(--color-soft)] px-4 py-2 text-sm text-[var(--color-ink)]"
            >
              {flash.notice}
            </div>
          {/if}
          {#if flash.alert}
            <div
              class="mt-2 rounded-[6px] border border-[var(--color-midnight)] bg-[var(--color-midnight)] px-4 py-2 text-sm text-white"
            >
              {flash.alert}
            </div>
          {/if}
        </div>
      {/if}

      <main class="min-w-0 flex-1 px-5 py-6 md:px-8">
        {@render children()}
      </main>
    </div>
  </div>
{/if}
